import DeliveryLocation from 'src/models/delivery-location/DeliveryLocation'
import MembershipTier from 'src/models/membership-type/MembershipTier'
import MembershipType from 'src/models/membership-type/MembershipType'
import Model, { encoders, ID, Integer, JsonObject, MODEL_LEVEL } from 'src/models/model'
import Archivable from 'src/models/model/concerns/Archivable'
import Librariable from 'src/models/model/concerns/Librariable'
import Tax from 'src/models/order/Tax'
import ShowingBadge from 'src/models/showing/ShowingBadge'
import TicketType from 'src/models/showing/TicketType'

export const TYPES = {
  ORDER: 'order',
  TICKET: 'ticket',
  ITEM: 'item'
}
TYPES.ALL = _.values(TYPES)
Object.freeze(TYPES)

export const CHANNELS = {
  EXTERNAL: 'external',
  KIOSK: 'kiosk',
  PHONE: 'phone',
  REGISTER: 'register',
  WEBSITE: 'website'
}
CHANNELS.ALL = _.values(CHANNELS)
Object.freeze(CHANNELS)

export const CONDITIONS = {
  PRICE: 'price',
  ORDER_ITEM_TYPES: 'order-item-types',
  MEMBERSHIP_TYPES: 'membership-types',
  MEMBERSHIP_TIERS: 'membership-tiers',
  SHOWING_BADGES: 'showing-badges',
  TICKET_TYPES: 'ticket-types',
  DELIVERY_LOCATIONS: 'delivery-locations'
}
CONDITIONS.ALL = _.values(CONDITIONS)
CONDITIONS[TYPES.ORDER] = _.without(CONDITIONS.ALL, CONDITIONS.TICKET_TYPES)
CONDITIONS[TYPES.TICKET] = _.without(CONDITIONS.ALL, CONDITIONS.ORDER_ITEM_TYPES, CONDITIONS.DELIVERY_LOCATIONS)
CONDITIONS[TYPES.ITEM] = [CONDITIONS.DELIVERY_LOCATIONS, CONDITIONS.MEMBERSHIP_TYPES, CONDITIONS.PRICE]
Object.freeze(CONDITIONS)

export const AMOUNT_STRATEGIES = {
  FIXED: 'fixed',
  PERCENTAGE: 'percentage'
}
AMOUNT_STRATEGIES.ALL = _.values(AMOUNT_STRATEGIES)

AMOUNT_STRATEGIES.BY_TYPE = {
  [TYPES.ORDER]: [AMOUNT_STRATEGIES.FIXED],
  [TYPES.TICKET]: [AMOUNT_STRATEGIES.FIXED, AMOUNT_STRATEGIES.PERCENTAGE],
  [TYPES.ITEM]: [AMOUNT_STRATEGIES.FIXED, AMOUNT_STRATEGIES.PERCENTAGE]
}
Object.freeze(AMOUNT_STRATEGIES)

export const CONDITIONS_REQUIREMENTS = {
  // ORDER_ITEM_TYPES
  ORDER_ITEM_TYPES: 'orderItemTypes',
  EXCLUDE_ORDER_ITEM_TYPES: 'excludeOrderItemTypes',
  // PRICE
  MIN_PRICE: 'minPrice',
  MAX_PRICE: 'maxPrice',
  EXCLUDE_PRICE: 'excludePrice',
  // MEMBERSHIP_TYPES
  MEMBERSHIP_TYPES: 'membershipTypeIds',
  EXCLUDE_MEMBERSHIP_TYPES: 'excludeMembershipTypes',
  // MEMBERSHIP_TIES
  MEMBERSHIP_TIERS: 'membershipTierIds',
  EXCLUDE_MEMBERSHIP_TIERS: 'excludeMembershipTiers',
  // SHOWING_BADGES
  SHOWING_BADGES: 'showingBadgeIds',
  EXCLUDE_SHOWING_BADGES: 'excludeShowingBadges',
  // TICKET_TYPES
  TICKET_TYPES: 'ticketTypeIds',
  EXCLUDE_TICKET_TYPES: 'excludeTicketTypes',
  // DELIVERY_LOCATIONS
  DELIVERY_LOCATIONS: 'deliveryLocationIds',
  EXCLUDE_DELIVERY_LOCATIONS: 'excludeDeliveryLocations'
}
CONDITIONS_REQUIREMENTS.ALL = _.values(CONDITIONS_REQUIREMENTS)
Object.freeze(CONDITIONS_REQUIREMENTS)

export default class BookingFeeType extends Model {
  static name = 'BookingFeeType'

  static level = MODEL_LEVEL.SITE
  static isConfiguration = true

  static deletable = true
  static duplicatable = true
  static paginate = true
  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Site]

  static define () {
    this.concerns = [Archivable, Librariable]

    this.fields = {
      id: {
        type: ID
      },
      name: {
        type: String,
        sortable: true,
        translatable: true
      },
      type: {
        type: String,
        default: TYPES.ORDER
      },
      enabled: {
        type: Boolean,
        default: true
      },
      priorityOrder: {
        type: Integer,
        default: 100
      },
      channels: {
        type: JsonObject,
        default: () => ({
          external: true,
          kiosk: false,
          phone: false,
          register: false,
          website: true
        })
      },
      amountStrategy: {
        type: String,
        default: AMOUNT_STRATEGIES.FIXED
      },
      percentThousandths: {
        type: Integer,
        default: 0
      },
      amount: {
        type: Number,
        encode: encoders.cents,
        default: 1
      },
      includeInPrice: {
        type: Boolean,
        default: false
      },
      refundable: {
        type: Boolean,
        default: true
      },
      refundByDefault: {
        type: Boolean,
        default: true
      },
      taxable: {
        type: Boolean,
        default: false
      },
      taxes: {
        type: [Tax],
        default: () => ([]),
        reference: true
      },
      conditions: {
        type: JsonObject,
        mergeDefault: true,
        default: () => _(CONDITIONS.ALL).keyBy().mapValues(() => false).value()
      },
      conditionsRequirements: {
        type: JsonObject,
        mergeDefault: true,
        default: () => ({
          orderItemTypes: [],
          excludeOrderItemTypes: false,
          minPrice: 0,
          maxPrice: null,
          excludePrice: false,
          excludeMembershipTypes: false,
          excludeMembershipTiers: false,
          excludeShowingBadges: false,
          excludeTicketTypes: false,
          excludeDeliveryLocations: false
        })
      },
      membershipTypes: {
        type: [MembershipType],
        default: () => ([]),
        reference: true
      },
      membershipTiers: {
        type: [MembershipTier],
        default: () => ([]),
        output: true
      },
      membershipTierIds: {
        type: [ID],
        default: () => ([])
      },
      showingBadges: {
        type: [ShowingBadge],
        default: () => ([]),
        reference: true
      },
      ticketTypes: {
        type: [TicketType],
        default: () => ([]),
        reference: true
      },
      deliveryLocations: {
        type: [DeliveryLocation],
        reference: true
      }
    }

    this.computed = {
      isActive () {
        return this.enabled && this.isUnarchived
      }
    }
  }
}
